// Catálogo de reportes del Portal Admin SureKeep (mock `portal-admin-sk-reportes.html`). Si el
// servicio no se puede leer, muestra por qué en lugar del catálogo; nunca datos de muestra.
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { toast } from 'sonner';
import { Skeleton, usePermissions } from '@/kit';
import { cn } from '@/lib/cn';
import { userMessageOf } from '@/api/errors';
import { SK_REPORT_CATALOG } from './catalog';
import {
    useDownloadJobCsv,
    useLakeCatalog,
    useReportJobs,
    useReportsServiceConfigured,
    useRunReport,
} from './api/useReports';
import {
    buildReportCards,
    buildRunEntries,
    CATEGORY_META,
    CATEGORY_ORDER,
    CATEGORY_TITLE,
    computeReportKpis,
    formatInt,
    groupCardsByCategory,
    resolveReportsAvailability,
} from './logic';
import { CREDIT_REPORTS_PERMISSIONS } from './permissions';
import { csvFilename, downloadCsv } from './lib/downloadCsv';
import { ReportCardTile } from './components/ReportCardTile';
import { ReportDetailModal } from './components/ReportDetailModal';
import { ReportRunsTable } from './components/ReportRunsTable';
import { ReportsServiceState } from './components/ReportsServiceState';
import { NoSource } from '@/shared/patterns/NoSource';
import type { ReportCard } from './types';

/** Valor de un KPI que todavía no se puede afirmar. */
const NO_VALUE = '—';

function Kpi({
    label,
    value,
    sub,
    valueClass,
    title,
}: {
    label: string;
    value: string | number;
    sub: ReactNode;
    valueClass?: string;
    title?: string;
}) {
    return (
        <div
            className="flex flex-none items-baseline gap-1.5 border-r border-border px-3.5 last:border-r-0"
            title={title}
        >
            <span className="text-[9.5px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
                {label}
            </span>
            <span className={cn('text-[14px] font-semibold tabular-nums', valueClass)}>
                {value}
            </span>
            <span className="text-[10px] text-muted-foreground">{sub}</span>
        </div>
    );
}

export function ReportsPage() {
    const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
    const [pendingRunId, setPendingRunId] = useState<string | null>(null);
    // Instante fijo al montar, para que la ventana de 30 días no cambie en cada render.
    const [mountedAt] = useState(() => Date.now());

    const { hasPermission } = usePermissions();
    const canRun = hasPermission(CREDIT_REPORTS_PERMISSIONS.queryRun);

    const configured = useReportsServiceConfigured();
    const lakeCatalog = useLakeCatalog(configured);
    const jobs = useReportJobs(lakeCatalog.isSuccess);
    const runReport = useRunReport();
    const downloadJobCsv = useDownloadJobCsv();

    const availability = resolveReportsAvailability({
        configured,
        isSuccess: lakeCatalog.isSuccess,
        error: lakeCatalog.error,
    });
    const ready = availability === 'ready';

    const tables = useMemo(() => lakeCatalog.data ?? [], [lakeCatalog.data]);
    const jobList = useMemo(() => jobs.data ?? [], [jobs.data]);

    const cards = useMemo(
        () => buildReportCards({ definitions: SK_REPORT_CATALOG, tables, jobs: jobList, canRun }),
        [tables, jobList, canRun],
    );
    const groups = useMemo(() => groupCardsByCategory(cards), [cards]);
    const runEntries = useMemo(() => buildRunEntries(jobList, SK_REPORT_CATALOG), [jobList]);
    const kpis = useMemo(
        () => computeReportKpis(SK_REPORT_CATALOG, jobList, mountedAt),
        [jobList, mountedAt],
    );

    const selected = cards.find((card) => card.definition.reportId === selectedReportId) ?? null;

    const run = (card: ReportCard) => {
        const { lakeTable } = card.definition;
        // Sin tabla no hay `ReportRequest` que mandar, aunque el botón ya esté apagado.
        if (card.blockedReason || !lakeTable) return;
        setPendingRunId(card.definition.reportId);
        runReport.mutate(
            { table: lakeTable },
            {
                onSuccess: (result) => {
                    setPendingRunId(null);
                    if (result.status === 'FAILED') {
                        toast.error('El reporte no se pudo generar', {
                            description: result.failureReason ?? 'El motor no dio un motivo.',
                        });
                        return;
                    }
                    toast.success(
                        result.status === 'DONE'
                            ? 'Reporte generado'
                            : 'Reporte en proceso, aparecerá en el log',
                    );
                },
                onError: (error) => {
                    setPendingRunId(null);
                    toast.error('No se pudo generar el reporte', {
                        description: userMessageOf(error),
                    });
                },
            },
        );
    };

    const downloadCsvOf = (card: ReportCard, jobId: string) => {
        downloadJobCsv.mutate(jobId, {
            onSuccess: (result) => {
                const filename = csvFilename(
                    card.definition.reportId,
                    card.lastRunAt ?? Date.now(),
                    result.jobId,
                );
                if (!downloadCsv(result.csv, filename)) {
                    toast.error('La corrida no produjo ningún archivo');
                    return;
                }
                toast.success('CSV descargado', { description: filename });
            },
            onError: (error) => {
                toast.error('No se pudo descargar el CSV', {
                    description: userMessageOf(error),
                });
            },
        });
    };

    const isLoading = availability === 'loading' || (ready && jobs.isPending);

    return (
        <div className="flex h-full min-h-0 flex-col">
            <div className="mt-2 flex-none rounded-t-[10px] border border-border bg-card px-3 py-2 text-[11px] leading-relaxed text-muted-foreground">
                ¿Qué reportes existen, cuándo corren y cuáles ya se generaron? Los automáticos
                corren en cron; los mensuales requieren corrida manual del Admin SK. El motor
                entrega el resultado en CSV — PDF no está implementado. Referencia M6_OPERACIONES
                §5.2.
            </div>

            <div className="flex flex-none items-center overflow-x-auto border-x border-b border-border bg-card px-3 py-2">
                <Kpi
                    label="Catálogo"
                    value={ready ? kpis.total : NO_VALUE}
                    sub="reportes disponibles"
                />
                <Kpi
                    label="Regulatorios"
                    value={ready ? kpis.regulatory : NO_VALUE}
                    valueClass="text-warning"
                    sub={
                        ready ? (
                            <NoSource reason="Ningún servicio publica el calendario regulatorio: no hay forma de contar los próximos a vencer." />
                        ) : (
                            'próximos a vencer'
                        )
                    }
                />
                <Kpi
                    label="Diarios · Cron"
                    value={ready ? kpis.daily : NO_VALUE}
                    valueClass="text-info"
                    sub="se generan solos"
                />
                <Kpi
                    label="Mensuales · manuales"
                    value={ready ? kpis.monthly : NO_VALUE}
                    sub="requieren corrida del Admin"
                />
                <Kpi
                    label="Corridas 30d"
                    value={
                        ready && jobs.isSuccess
                            ? `${kpis.runsTruncated ? '≥ ' : ''}${formatInt(kpis.runsLast30d)}`
                            : NO_VALUE
                    }
                    sub="todas las categorías"
                    title={
                        kpis.runsTruncated
                            ? 'El historial llegó topado en 50 corridas: este número es un piso, no el total.'
                            : undefined
                    }
                />
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto rounded-b-[10px] border-x border-b border-border bg-background p-2">
                {availability === 'unavailable' ||
                availability === 'forbidden' ||
                availability === 'failed' ? (
                    <ReportsServiceState
                        kind={availability}
                        detail={
                            availability === 'failed' ? userMessageOf(lakeCatalog.error) : undefined
                        }
                        onRetry={() => {
                            void lakeCatalog.refetch();
                        }}
                    />
                ) : (
                    <>
                        {groups.map((group, index) => (
                            <section
                                key={group.category}
                                className="mb-2 rounded-lg border border-border bg-card"
                            >
                                <div className="flex items-baseline justify-between gap-3 border-b border-border px-3 py-2">
                                    <span className="text-[11.5px] font-semibold uppercase tracking-[0.04em]">
                                        {index + 1} · {CATEGORY_TITLE[group.category]}
                                    </span>
                                    <span className="text-right text-[10.5px] text-muted-foreground">
                                        {CATEGORY_META[group.category]}
                                    </span>
                                </div>
                                <div className="grid grid-cols-1 gap-2 p-2 md:grid-cols-2">
                                    {isLoading
                                        ? Array.from({ length: 2 }, (_unused, i) => (
                                              <div
                                                  key={i}
                                                  className="rounded-md border border-border p-3"
                                              >
                                                  <Skeleton lines={4} />
                                              </div>
                                          ))
                                        : group.cards.map((card) => (
                                              <ReportCardTile
                                                  key={card.definition.reportId}
                                                  card={card}
                                                  isRunning={
                                                      pendingRunId === card.definition.reportId
                                                  }
                                                  onOpen={() => {
                                                      setSelectedReportId(card.definition.reportId);
                                                  }}
                                                  onRun={() => {
                                                      run(card);
                                                  }}
                                              />
                                          ))}
                                </div>
                            </section>
                        ))}

                        <section className="rounded-lg border border-border bg-card">
                            <div className="flex items-baseline justify-between gap-3 border-b border-border px-3 py-2">
                                <span className="text-[11.5px] font-semibold uppercase tracking-[0.04em]">
                                    {CATEGORY_ORDER.length + 1} · Log · corridas y descargas
                                    recientes
                                </span>
                                <span className="text-right text-[10.5px] text-muted-foreground">
                                    Últimas corridas dentro del alcance del Admin SK · el motor no
                                    guarda el archivo: para tenerlo hay que volver a pedirlo
                                </span>
                            </div>
                            <ReportRunsTable entries={runEntries} isPending={isLoading} />
                        </section>
                    </>
                )}
            </div>

            <ReportDetailModal
                card={selected}
                isRunning={pendingRunId !== null}
                isDownloading={downloadJobCsv.isPending}
                onClose={() => {
                    setSelectedReportId(null);
                }}
                onRun={() => {
                    if (selected) run(selected);
                }}
                onDownloadCsv={(jobId) => {
                    if (selected) downloadCsvOf(selected, jobId);
                }}
            />
        </div>
    );
}
